import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import AuthModal from '../../../components/auth/AuthModal';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    // 홈 페이지로 이동 후 스크롤
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleAuthClick = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link 
              to="/"
              className="flex items-center space-x-2 cursor-pointer"
            >
              <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                <i className="ri-plant-line text-white text-xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">고흥꿈트리</h1>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-gray-700 hover:text-green-600 font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                홈
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-700 hover:text-green-600 font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                소개
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-green-600 font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                프로그램
              </button>
              <Link
                to="/partners"
                className="text-gray-700 hover:text-green-600 font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                참여업체
              </Link>
              <Link
                to="/gallery"
                className="text-gray-700 hover:text-green-600 font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                갤러리
              </Link>
              <Link
                to="/inquiry"
                className="text-gray-700 hover:text-green-600 font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                문의
              </Link>

              {/* 로그인/회원가입 버튼 */}
              {!user ? (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleAuthClick('login')}
                    className="px-4 py-2 text-gray-700 hover:text-green-600 font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    로그인
                  </button>
                  <button
                    onClick={() => handleAuthClick('signup')}
                    className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    회원가입
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 px-4 py-2 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-white"></i>
                    </div>
                    <span className="text-gray-700 font-semibold whitespace-nowrap">
                      {user.user_metadata?.name || '사용자'}
                    </span>
                    <i className={`ri-arrow-down-s-line text-gray-600 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* 사용자 메뉴 드롭다운 */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer flex items-center space-x-2"
                      >
                        <i className="ri-dashboard-line"></i>
                        <span className="whitespace-nowrap">대시보드</span>
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center space-x-2"
                      >
                        <i className="ri-logout-box-line"></i>
                        <span className="whitespace-nowrap">로그아웃</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </nav>

            <button className="md:hidden p-2 text-gray-700 hover:text-green-600 cursor-pointer">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* 인증 모달 */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode={authMode}
      />
    </>
  );
}
